import React, { useMemo } from 'react';
import { cn } from '@shared/lib/utils';
import { DetailsList, DetailRow } from '../../components';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@shared/components/ui/accordion';
import { useSectionTitleClassName } from '../../context/SectionStyleContext';
import {
  PAYMENT_DETAILS,
  FEES_DETAILS,
  PAYMENT_SECURITY_DETAILS,
  PAYMENT_METHOD_TAB_DETAILS,
} from '@shared/data';
import { PaymentAdapter } from '@shared/adapters';
import type { PaymentDetail } from '../../types/panel-common.types';
import type { Transaction } from '@shared/types/payment.types';

// ── Fees filtering helpers ────────────────────────────────────────────────

const isZeroValue = (value: string): boolean => {
  const cleanValue = value.replace(/[A-Z]{3}|\s|€|£|¥|\$|,/g, '').trim();
  const numericValue = parseFloat(cleanValue);
  return numericValue === 0 || cleanValue === '' || cleanValue === '0';
};

const filterFees = (details: PaymentDetail[]): PaymentDetail[] =>
  details.filter(fee => !isZeroValue(fee.value));

// ── FoldableDetails ───────────────────────────────────────────────────────

interface FoldableDetailsProps {
  details: PaymentDetail[];
  isDetailView?: boolean;
  specialRenderer?: (detail: PaymentDetail, index: number, isLast: boolean) => React.ReactNode | null;
}

const FoldableDetails = React.memo<FoldableDetailsProps>(({
  details,
  isDetailView = false,
  specialRenderer,
}) => {
  return (
    <div className="flex flex-col pt-2">
      <DetailsList
        details={details}
        isDetailView={isDetailView}
        specialRenderer={specialRenderer}
        align="right"
      />
    </div>
  );
});

FoldableDetails.displayName = 'FoldableDetails';

// ── Shared data hook (single source of truth for both components) ─────────

function usePaymentTabData(isDetailView: boolean, transactionData?: Transaction) {
  const details = useMemo(() => {
    if (transactionData) {
      return new PaymentAdapter().adapt(transactionData);
    }
    return PAYMENT_DETAILS;
  }, [transactionData]);

  const filteredFees = useMemo(() => filterFees(FEES_DETAILS), []);

  const feesSpecialRenderer = useMemo(() => {
    return (detail: PaymentDetail, _index: number, _isLast: boolean): React.ReactNode | null => {
      if (detail.label !== 'Net settlement amount (EUR)') return null;
      const parts = detail.value.match(/^([\d,.\s]+)\s*\((.*?)\)\s*(\w+)$/);
      const mainValue = parts ? `${parts[3]} ${parts[1].trim()}` : detail.value;
      return (
        <DetailRow
          label="Net settlement amount"
          value={mainValue}
          isDetailView={isDetailView}
          align="right"
        />
      );
    };
  }, [isDetailView]);

  const contentMap: Record<PaymentTabKey, React.ReactNode> = useMemo(() => ({
    details: <FoldableDetails details={details} isDetailView={isDetailView} />,
    method: <FoldableDetails details={PAYMENT_METHOD_TAB_DETAILS} isDetailView={isDetailView} />,
    security: <FoldableDetails details={PAYMENT_SECURITY_DETAILS} isDetailView={isDetailView} />,
    fees: <FoldableDetails details={filteredFees} isDetailView={isDetailView} specialRenderer={feesSpecialRenderer} />,
  }), [details, filteredFees, feesSpecialRenderer, isDetailView]);

  return contentMap;
}

// ── Tab keys & labels ─────────────────────────────────────────────────────

export const PAYMENT_TAB_KEYS = ['details', 'method', 'security', 'fees'] as const;
export type PaymentTabKey = (typeof PAYMENT_TAB_KEYS)[number];

// eslint-disable-next-line react-refresh/only-export-components
export const PAYMENT_TAB_LABELS: Record<PaymentTabKey, string> = {
  details: 'Details',
  method: 'Method',
  security: 'Security',
  fees: 'Fees',
};

// Fuller labels used when the section is rendered as stacked accordion rows.
// eslint-disable-next-line react-refresh/only-export-components
export const PAYMENT_ACCORDION_LABELS: Record<PaymentTabKey, string> = {
  details: 'Payment Details',
  method: 'Payment Method',
  security: 'Security',
  fees: 'Fees',
};

// ── Payment (self-contained tabbed section) ───────────────────────────────

interface PaymentProps {
  isDetailView?: boolean;
  transactionData?: Transaction;
  className?: string;
}

export const Payment = React.memo<PaymentProps>(({
  isDetailView = false,
  transactionData,
  className,
}) => {
  const contentMap = usePaymentTabData(isDetailView, transactionData);
  const sectionTitleClassName = useSectionTitleClassName();

  return (
    <section className={cn('flex flex-col', className)} aria-labelledby="payment-section-heading">
      <h2 id="payment-section-heading" className={cn(sectionTitleClassName, 'mb-3')}>
        Payment
      </h2>

      {/* One row per category, collapsed by default, single-open (one at a time) */}
      <Accordion type="single" collapsible>
        {PAYMENT_TAB_KEYS.map((key) => (
          <AccordionItem key={key} value={key}>
            <AccordionTrigger className="py-3 font-medium">
              {PAYMENT_ACCORDION_LABELS[key]}
            </AccordionTrigger>
            <AccordionContent className="pb-4 pl-1">
              {contentMap[key]}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </section>
  );
});

Payment.displayName = 'Payment';

// ── Standalone tab content (for split header/content layouts) ─────────────

interface PaymentTabContentProps {
  activeTab: PaymentTabKey;
  isDetailView?: boolean;
  transactionData?: Transaction;
}

export const PaymentTabContent = React.memo<PaymentTabContentProps>(({
  activeTab,
  isDetailView = false,
  transactionData,
}) => {
  const contentMap = usePaymentTabData(isDetailView, transactionData);
  return <>{contentMap[activeTab]}</>;
});

PaymentTabContent.displayName = 'PaymentTabContent';
